import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material'
import api from '../services/api'
import type { IntegrationConfig, ServiceType } from '../types/integration'

interface GroupedIntegrations {
  [key: string]: IntegrationConfig[]
}

const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  email: 'Email Services',
  whatsapp: 'WhatsApp Services',
  sms: 'SMS Services',
  logistics: 'Logistics Services',
  payment: 'Payment Services',
}

const IntegrationConfigPage: React.FC = () => {
  const navigate = useNavigate()
  
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  
  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationConfig | null>(null)
  const [testDialogOpen, setTestDialogOpen] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    loadIntegrations()
  }, [])

  const loadIntegrations = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get('/admin/integrations/configs')
      setIntegrations(response.data.configs || [])
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load integrations')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = (integration: IntegrationConfig) => {
    setSelectedIntegration(integration)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedIntegration) return
    
    try {
      await api.delete(`/admin/integrations/configs/${selectedIntegration.id}`)
      setSuccess(`Integration ${selectedIntegration.provider_name} deleted successfully`)
      setDeleteDialogOpen(false)
      setSelectedIntegration(null)
      loadIntegrations()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete integration')
    }
  }

  const handleTestClick = (integration: IntegrationConfig) => {
    setSelectedIntegration(integration)
    setTestResult(null)
    setTestDialogOpen(true)
  }

  const handleTestIntegration = async () => {
    if (!selectedIntegration) return
    
    try {
      setTestLoading(true)
      setTestResult(null)
      
      const response = await api.post(
        `/admin/integrations/configs/${selectedIntegration.id}/test`
      )
      
      setTestResult({
        success: true,
        message: response.data.message || 'Integration test successful'
      })
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Integration test failed'
      })
    } finally {
      setTestLoading(false)
    }
  }

  const getStatusColor = (status: string): 'success' | 'error' | 'default' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success'
      case 'error':
        return 'error'
      case 'maintenance':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircleIcon fontSize="small" />
      case 'error':
        return <ErrorIcon fontSize="small" />
      case 'maintenance':
        return <WarningIcon fontSize="small" />
      default:
        return null
    }
  }

  const filterIntegrations = (integrations: IntegrationConfig[]): IntegrationConfig[] => {
    return integrations.filter((integration) => {
      const matchesSearch = searchTerm === '' || 
        integration.provider_name.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesServiceType = serviceTypeFilter === 'all' || 
        integration.service_type === serviceTypeFilter
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && integration.is_active) ||
        (statusFilter === 'inactive' && !integration.is_active)
      
      return matchesSearch && matchesServiceType && matchesStatus
    })
  }

  const groupIntegrationsByType = (integrations: IntegrationConfig[]): GroupedIntegrations => {
    const grouped: GroupedIntegrations = {}
    
    integrations.forEach((integration) => {
      const type = integration.service_type
      if (!grouped[type]) {
        grouped[type] = []
      }
      grouped[type].push(integration)
    })
    
    // Sort by priority within each group
    Object.keys(grouped).forEach((type) => {
      grouped[type].sort((a, b) => a.priority - b.priority)
    })
    
    return grouped
  }

  const filteredIntegrations = filterIntegrations(integrations)
  const groupedIntegrations = groupIntegrationsByType(filteredIntegrations)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Integration Configurations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage external service integrations and their configurations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/integrations/config/create')}
        >
          Add Integration
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by provider name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <TextField
            select
            label="Service Type"
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Services</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="logistics">Logistics</MenuItem>
            <MenuItem value="payment">Payment</MenuItem>
          </TextField>
          
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          
          <IconButton onClick={loadIntegrations} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Integrations Grouped by Service Type */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : Object.keys(groupedIntegrations).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No integrations found
          </Typography>
        </Paper>
      ) : (
        Object.entries(groupedIntegrations).map(([serviceType, serviceIntegrations]) => (
          <Accordion key={serviceType} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="h6">
                  {SERVICE_TYPE_LABELS[serviceType as ServiceType] || serviceType}
                </Typography>
                <Chip 
                  label={`${serviceIntegrations.length} integration${serviceIntegrations.length !== 1 ? 's' : ''}`}
                  size="small"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Health</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Last Health Check</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceIntegrations.map((integration) => (
                      <TableRow key={integration.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {integration.provider_name}
                            </Typography>
                            {integration.is_primary && (
                              <Chip label="Primary" size="small" color="primary" sx={{ mt: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={integration.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={integration.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(integration.health_status)}
                            label={integration.health_status}
                            size="small"
                            color={getStatusColor(integration.health_status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={`Priority ${integration.priority}`} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {integration.last_health_check
                            ? new Date(integration.last_health_check).toLocaleString()
                            : 'Never'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Test Integration">
                            <IconButton
                              size="small"
                              onClick={() => handleTestClick(integration)}
                              color="primary"
                            >
                              <TestIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Integration">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/integrations/config/${integration.id}/edit`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Integration">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(integration)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Integration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the integration <strong>{selectedIntegration?.provider_name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Integration Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Test Integration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test the connection and configuration for <strong>{selectedIntegration?.provider_name}</strong>
          </Typography>
          
          {testResult && (
            <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
              {testResult.message}
            </Alert>
          )}
          
          {testLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          <Button
            onClick={handleTestIntegration}
            variant="contained"
            disabled={testLoading}
            startIcon={<TestIcon />}
          >
            Run Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default IntegrationConfigPage
